import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { Session, User } from '@supabase/supabase-js';
import { deriveKey, generateSalt, hashPassword } from '@/lib/crypto';
import { localDB } from '@/lib/storage';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  error: string | null;
  encryptionKey: CryptoKey | null;
  signUp: (email: string, password: string, masterPassword: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string, masterPassword: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  unlockVault: (masterPassword: string) => Promise<{ success: boolean; error?: string }>;
  isVaultUnlocked: boolean;
  retryInitialization: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [encryptionKey, setEncryptionKey] = useState<CryptoKey | null>(null);
  const [isVaultUnlocked, setIsVaultUnlocked] = useState(false);

  const initializeAuth = async () => {
    try {
      setLoading(true);
      setError(null);

      if (localDB.isSupported()) {
        try {
          await Promise.race([
            localDB.init(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Database initialization timeout')), 10000)
            )
          ]);
        } catch (dbError) {
          console.warn('IndexedDB initialization failed:', dbError);
        }
      }

      const { data: { session }, error: sessionError } = await Promise.race([
        supabase.auth.getSession(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Session check timeout')), 10000)
        )
      ]);

      if (sessionError) {
        throw sessionError;
      }

      setSession(session);
      setUser(session?.user ?? null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const retryInitialization = async () => {
    await initializeAuth();
  };

  useEffect(() => {
    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (!session) {
        setEncryptionKey(null);
        setIsVaultUnlocked(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, masterPassword: string) => {
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('User creation failed');

      const salt = await generateSalt();
      const masterPasswordHash = await hashPassword(masterPassword, salt);

      const { error: profileError } = await supabase
        .from('user_profiles')
        .insert({
          id: authData.user.id,
          master_password_hash: masterPasswordHash,
          encryption_salt: salt,
        });

      if (profileError) throw profileError;

      const key = await deriveKey(masterPassword, salt);
      setEncryptionKey(key);
      setIsVaultUnlocked(true);

      if (localDB.isInitialized()) {
        await localDB.setMetadata('encryption_salt', salt).catch(console.error);
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string, masterPassword: string) => {
    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Sign in failed');

      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('encryption_salt, master_password_hash')
        .eq('id', authData.user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      if (!profile) throw new Error('User profile not found');

      const masterPasswordHash = await hashPassword(masterPassword, profile.encryption_salt);

      if (masterPasswordHash !== profile.master_password_hash) {
        throw new Error('Invalid master password');
      }

      const key = await deriveKey(masterPassword, profile.encryption_salt);
      setEncryptionKey(key);
      setIsVaultUnlocked(true);

      if (localDB.isInitialized()) {
        await localDB.setMetadata('encryption_salt', profile.encryption_salt).catch(console.error);
      }

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const unlockVault = async (masterPassword: string) => {
    try {
      if (!user) {
        return { success: false, error: 'No user logged in' };
      }

      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('encryption_salt, master_password_hash')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      if (!profile) throw new Error('User profile not found');

      const masterPasswordHash = await hashPassword(masterPassword, profile.encryption_salt);

      if (masterPasswordHash !== profile.master_password_hash) {
        return { success: false, error: 'Invalid master password' };
      }

      const key = await deriveKey(masterPassword, profile.encryption_salt);
      setEncryptionKey(key);
      setIsVaultUnlocked(true);

      return { success: true };
    } catch (error) {
      return { success: false, error: (error as Error).message };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setEncryptionKey(null);
    setIsVaultUnlocked(false);
    if (localDB.isInitialized()) {
      await localDB.clearAll().catch(console.error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        error,
        encryptionKey,
        signUp,
        signIn,
        signOut,
        unlockVault,
        isVaultUnlocked,
        retryInitialization,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
